import { api } from "@/utils/axiosInterceptor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "./use-toast";
import type {
  MappingProfile,
  MappingProfileColumn,
  MappingProfileVersion,
  MappingProfileAuditLog,
  MappingProfileListResponse,
  CreateMappingProfileRequest,
  UpdateMappingProfileRequest,
  UpdateColumnsRequest,
  AnalyzeResponse,
  TestPreviewResult,
} from "@/types/mappingProfile";

/**
 * Build a readable error message
 */
const formatProfileError = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (Array.isArray(data?.details) && data.details.length > 0) {
    const lines = data.details.map(
      (d: any) => `• ${d.path ? `${d.path}: ` : ""}${d.message}`,
    );
    return `${data.message || "Validation failed"}\n${lines.join("\n")}`;
  }
  return data?.message || fallback;
};

// ─── List Profiles ───────────────────────────────────────────────────────────

export const useMappingProfiles = (query?: {
  templateType?: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  const params = new URLSearchParams();
  if (query?.templateType) params.set("templateType", query.templateType);
  if (query?.page) params.set("page", String(query.page));
  if (query?.limit) params.set("limit", String(query.limit));
  if (query?.search) params.set("search", query.search);

  const queryString = params.toString();
  const url = queryString
    ? `/mapping-profiles?${queryString}`
    : "/mapping-profiles";

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["mappingProfiles", query],
    queryFn: async () => {
      const response = await api.get<MappingProfileListResponse>(url);
      return response.data;
    },
  });

  return {
    profiles: data?.data ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    refetch,
  };
};

// ─── Get Single Profile ──────────────────────────────────────────────────────

export const useMappingProfile = (profileId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["mappingProfile", profileId],
    queryFn: async () => {
      const response = await api.get<
        MappingProfile & { columns: MappingProfileColumn[] }
      >(`/mapping-profiles/${profileId}`);
      return response.data;
    },
    enabled: !!profileId,
  });

  return {
    profile: data ?? null,
    columns: data?.columns ?? [],
    isLoading,
  };
};

// ─── Create Profile ──────────────────────────────────────────────────────────

export const useCreateMappingProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async (data: CreateMappingProfileRequest) => {
      const response = await api.post<MappingProfile>(
        "/mapping-profiles",
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mappingProfiles"] });
      toast({
        title: "Profile Created",
        description: "Mapping profile has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Creation Failed",
        description: formatProfileError(
          error,
          "Failed to create mapping profile",
        ),
        variant: "destructive",
        duration: 15000,
      });
    },
  });

  return {
    createProfile: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
};

// ─── Update Profile ──────────────────────────────────────────────────────────

export const useUpdateMappingProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateMappingProfileRequest;
    }) => {
      const response = await api.put<MappingProfile>(
        `/mapping-profiles/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["mappingProfiles"] });
      queryClient.invalidateQueries({
        queryKey: ["mappingProfile", variables.id],
      });
      toast({
        title: "Profile Updated",
        description: "Mapping profile has been updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: formatProfileError(
          error,
          "Failed to update mapping profile",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    updateProfile: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
};

// ─── Delete Profile ──────────────────────────────────────────────────────────

export const useDeleteMappingProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/mapping-profiles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mappingProfiles"] });
      toast({
        title: "Profile Deleted",
        description: "Mapping profile has been deleted.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: formatProfileError(
          error,
          "Failed to delete mapping profile",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    deleteProfile: mutation.mutateAsync,
    isDeleting: mutation.isPending,
  };
};

// ─── Clone Profile ───────────────────────────────────────────────────────────

export const useCloneMappingProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name?: string }) => {
      const response = await api.post<MappingProfile>(
        `/mapping-profiles/${id}/clone`,
        name ? { name } : {},
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mappingProfiles"] });
      toast({
        title: "Profile Cloned",
        description: "A copy of the mapping profile has been created.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Clone Failed",
        description: formatProfileError(
          error,
          "Failed to clone mapping profile",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    cloneProfile: mutation.mutateAsync,
    isCloning: mutation.isPending,
  };
};

// ─── Update Columns ──────────────────────────────────────────────────────────

export const useUpdateMappingColumns = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateColumnsRequest;
    }) => {
      const response = await api.put(`/mapping-profiles/${id}/columns`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["mappingProfile", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["mappingProfileVersions", variables.id],
      });
      toast({
        title: "Columns Updated",
        description: "Column mappings saved — new version created.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: formatProfileError(
          error,
          "Failed to update column mappings",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    updateColumns: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
};

// ─── Versions ────────────────────────────────────────────────────────────────

export const useMappingProfileVersions = (profileId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["mappingProfileVersions", profileId],
    queryFn: async () => {
      const response = await api.get<MappingProfileVersion[]>(
        `/mapping-profiles/${profileId}/versions`,
      );
      return response.data;
    },
    enabled: !!profileId,
  });

  return {
    versions: data ?? [],
    isLoading,
  };
};

export const useRollbackVersion = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      versionNumber,
    }: {
      id: string;
      versionNumber: number;
    }) => {
      const response = await api.post(`/mapping-profiles/${id}/rollback`, {
        versionNumber,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["mappingProfile", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["mappingProfileVersions", variables.id],
      });
      toast({
        title: "Rollback Successful",
        description: `Reverted to version ${variables.versionNumber}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Rollback Failed",
        description: formatProfileError(error, "Failed to rollback"),
        variant: "destructive",
      });
    },
  });

  return {
    rollback: mutation.mutateAsync,
    isRollingBack: mutation.isPending,
  };
};

// ─── Analyze (Upload + metadata extraction) ──────────────────────────────────

export const useAnalyzeFile = () => {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      file,
      dataStartRow,
      templateType,
    }: {
      file: File;
      dataStartRow?: number;
      templateType?: string;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (dataStartRow) {
        formData.append("dataStartRow", String(dataStartRow));
      }
      if (templateType) {
        formData.append("templateType", templateType);
      }

      const response = await api.post<AnalyzeResponse>(
        "/mapping-profiles/analyze",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    },
    onError: (error: any) => {
      toast({
        title: "Analysis Failed",
        description: formatProfileError(
          error,
          "Failed to analyze the uploaded file",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    analyzeFile: mutation.mutateAsync,
    isAnalyzing: mutation.isPending,
    analysisResult: mutation.data ?? null,
  };
};

// ─── Test Mapping ────────────────────────────────────────────────────────────

export const useTestMapping = () => {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      profileId,
      file,
    }: {
      profileId: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post<TestPreviewResult>(
        `/mapping-profiles/${profileId}/test`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    },
    onError: (error: any) => {
      toast({
        title: "Test Failed",
        description: formatProfileError(
          error,
          "Failed to test mapping profile",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    testMapping: mutation.mutateAsync,
    isTesting: mutation.isPending,
    testResult: mutation.data ?? null,
  };
};

// ─── Download Converted Template ─────────────────────────────────────────────

export const useDownloadConverted = () => {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      profileId,
      file,
    }: {
      profileId: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/mapping-profiles/${profileId}/convert`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        },
      );

      const contentDisposition = response.headers["content-disposition"];
      let filename = "converted_template.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error: any) => {
      toast({
        title: "Download Failed",
        description: formatProfileError(
          error,
          "Failed to generate converted template",
        ),
        variant: "destructive",
      });
    },
  });

  return {
    downloadConverted: mutation.mutateAsync,
    isDownloading: mutation.isPending,
  };
};

// ─── Audit Log ───────────────────────────────────────────────────────────────

export const useMappingProfileAudit = (
  profileId: string,
  query?: { page?: number; limit?: number },
) => {
  const { data, isLoading } = useQuery({
    queryKey: ["mappingProfileAudit", profileId, query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query?.page) params.set("page", String(query.page));
      if (query?.limit) params.set("limit", String(query.limit));
      const qs = params.toString();
      const url = qs
        ? `/mapping-profiles/${profileId}/audit?${qs}`
        : `/mapping-profiles/${profileId}/audit`;

      const response = await api.get<{
        data: MappingProfileAuditLog[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(url);
      return response.data;
    },
    enabled: !!profileId,
  });

  return {
    auditLogs: data?.data ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
  };
};
